import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import type { RootState } from '@/redux/store';
import {
	assignExperimentService,
	getExperimentAssignmentsService,
	type ExperimentAssignment,
} from '@/shared/services/experiments.service';

export const EXPERIMENT_ASSIGNMENTS_QUERY_KEY = ['bootstrap', 'experiments'] as const;

/**
 * The authenticated user's frozen arms. Fetched in the bootstrap of every
 * authenticated load; an experiment with an assignment here NEVER evaluates
 * GrowthBook again. It only changes through our own writes (which update this
 * cache) or through an override, which reloads the page.
 */
export const useExperimentAssignments = () => {
	const userId = useSelector((state: RootState) => state.profile.user?.id);
	return useQuery({
		queryKey: EXPERIMENT_ASSIGNMENTS_QUERY_KEY,
		queryFn: getExperimentAssignmentsService,
		enabled: !!userId,
		staleTime: Infinity,
	});
};

const withAssignment = (
	assignments: ExperimentAssignment[] | undefined,
	assignment: ExperimentAssignment,
) => [
	...(assignments ?? []).filter(
		(current) => current.experiment_id !== assignment.experiment_id,
	),
	assignment,
];

/**
 * Freezes an exposure in the back. The cache is written before the response so
 * the surface follows the arm in the same render, and is then corrected with
 * the one the server returns: if one was already frozen, that one rules.
 */
export const useAssignExperiment = () => {
	const queryClient = useQueryClient();
	const { mutate } = useMutation({
		mutationFn: assignExperimentService,
		onMutate: (body) => {
			queryClient.setQueryData<ExperimentAssignment[]>(
				EXPERIMENT_ASSIGNMENTS_QUERY_KEY,
				(assignments) =>
					withAssignment(assignments, {
						...body,
						assigned_at: new Date().toISOString(),
					}),
			);
		},
		onSuccess: (assignment) => {
			queryClient.setQueryData<ExperimentAssignment[]>(
				EXPERIMENT_ASSIGNMENTS_QUERY_KEY,
				(assignments) => withAssignment(assignments, assignment),
			);
		},
	});
	return mutate;
};
